import { useHints } from '@wt/game/providers/hints/HintsProvider';
import { useCallback, useState } from 'react';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import ImageModal from '@wt/game/modals/ImageModal';
import HintButton from './HintButton';
import CrateButton from './CrateButton';
import { createPortal } from 'react-dom';
import { Spinner } from '@wt/shared/components/spinner/spinner';
import { smallButtonClasses } from '@wt/shared/components/buttons/SmallButton';
import { getSiteMode } from '@wt/utilities/siteMode';
import { getDistanceText } from '@wt/app/game/_utils/getGameScore';
import useSettingsContext from '@wt/game/providers/settings/types';
import { useUserProvider } from '@wt/game/providers/user/UserProvider';

type HintInfo = {
  hint: string;
  label: string;
  disabled: boolean;
};

const HintBox = (props: {
  title: string;
  type: string;
  hints: HintInfo[];
  mode: 'results' | 'game';
}) => {
  const [hintWait, setHintWait] = useState(false);
  const hints = useHints();
  const { user } = useUserProvider();

  const usedHint = hints.getUsedHints(props.type).find(() => true) ?? null;
  const hintData = usedHint ? hints.getHintData(usedHint) : null;

  const [selectedHint, setSelectedHint] = useState<string | null>(
    usedHint ?? (props.hints.length == 1 ? props.hints[0].hint : null)
  );

  const [settings] = useSettingsContext();

  const boxStyle = {
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.2)',
  };
  const frameStyle =
    getSiteMode() == 'whentaken-movies'
      ? { boxShadow: '0px 0px 10px rgba(255, 194, 76, 0.5)' }
      : {
          filter: 'drop-shadow(0px 0px 3px rgba(106, 9, 246, 0.8))',
        };

  const scrollIntoView = useCallback((node: HTMLElement | null) => {
    if (node) node.scrollIntoView();
  }, []);

  return (
    <>
      <div
        style={boxStyle}
        className="py-6 px-9 rounded-lg bg-secondary movies:dark:bg-primary-content"
      >
        <div className="text-base md:text-2xl text-center">{props.title}</div>
        <div className="flex flex-wrap w-full flex-row items-center justify-center gap-4 mt-5">
          {props.hints.map((h) => {
            return (
              <HintButton
                key={h.hint}
                handleSelect={() => {
                  setSelectedHint(h.hint);
                }}
                selected={selectedHint == h.hint}
                disabled={
                  h.disabled ||
                  (selectedHint != h.hint && hints.hasUsedHintType(props.type))
                }
              >
                {h.label}
              </HintButton>
            );
          })}
        </div>
        {hintData?.selectedBox == -1 && (
          <>
            <div className="text-center mt-4 text-base">Choose your box!</div>
            <div
              className="flex justify-center mt-3 gap-3 text-5xl"
              ref={scrollIntoView}
            >
              {[0, 1, 2].map((selectedBox) => (
                <CrateButton
                  key={selectedBox}
                  i={selectedBox}
                  handleSelect={() => {
                    if (props.mode == 'game' && hintData?.selectedBox == -1) {
                      hints.setHintData(usedHint!, {
                        ...hintData,
                        selectedBox,
                      });
                    }
                  }}
                />
              ))}
            </div>
          </>
        )}
        {hintData?.selectedBox >= 0 && (hintData?.text || hintData?.image) && (
          <>
            <div className="text-base text-center mt-4 p-2 rounded-lg bg-base-200 outline outline-2 outline-accent">
              {hintData?.text && <div>{hintData?.text}</div>}
              {hintData?.image && (
                <>
                  <div
                    className="relative overflow-hidden rounded-md md:rounded-xl w-full h-72 mt-2 flex items-center justify-center bg-cover bg-center"
                    style={{ backgroundImage: `url(${hintData.image})` }}
                    ref={scrollIntoView}
                    onClick={() => {
                      const target = document.getElementById(
                        props.type + '-hint-image-modal'
                      );
                      if (target && target instanceof HTMLElement) {
                        target.click();
                      }
                    }}
                  >
                    <div
                      className={`absolute top-0 right-0 mt-4 mr-4 ${smallButtonClasses}`}
                    >
                      <FullscreenRoundedIcon htmlColor="black" />
                    </div>
                  </div>
                  {createPortal(
                    <div style={{ zIndex: 1400 }}>
                      <ImageModal
                        id={props.type + '-hint-image-modal'}
                        src={hintData.image}
                        alt={''}
                      />
                    </div>,
                    document.body,
                    props.type + '-hint-image-modal'
                  )}
                </>
              )}
              {usedHint == 'proximitypic' &&
                props.mode == 'results' &&
                hintData?.locationHint && (
                  <div className="text-left">
                    <div className="flex items-center mt-2 text-xl">
                      <div className="text-2xl px-2">📍</div>
                      <div className="flex-1 uppercase">
                        {hintData.locationHint.city +
                          ', ' +
                          hintData.locationHint.country}
                      </div>
                    </div>
                    <div className="mt-2 font-semibold text-highlight">
                      Distance:{' '}
                      {getDistanceText(
                        hintData.locationHint.distance * 1000,
                        settings?.distanceUnit,
                        false
                      )}{' '}
                      (from main photo)
                    </div>
                    {hintData.locationHint.description && (
                      <div className="mt-3">
                        {hintData.locationHint.description}
                      </div>
                    )}
                  </div>
                )}
            </div>
          </>
        )}
        {usedHint == 'dualdrop' && (
          <>
            <div
              style={frameStyle}
              className="text-base text-center mt-4 py-1 px-2 rounded-lg bg-base-200 outline outline-2 outline-accent"
            >
              <div>
                Use the toggle on the map to switch between the two pins. Your
                score will be based on the closer guess.
              </div>
              <div
                className="cursor-pointer"
                ref={scrollIntoView}
                onClick={() => {
                  hints.setHintsModalOpen(false);
                  hints.setTrigger('selectMap');
                }}
              >
                {getSiteMode() == 'whentaken-movies' && (
                  <img
                    src={'/assets/images/dualdrop-movies.gif'}
                    className="rounded-xl m-auto"
                    alt=""
                  />
                )}
                {getSiteMode() != 'whentaken-movies' && (
                  <img
                    src={'/assets/images/dualdrop.gif'}
                    className="rounded-3xl m-auto"
                    alt=""
                  />
                )}
              </div>
            </div>
          </>
        )}
        {(!usedHint || !hintData) && (
          <div
            style={frameStyle}
            className="text-base text-center mt-4 py-1 px-2 rounded-lg bg-base-200 outline outline-2 outline-accent"
          >
            {selectedHint == 'proximitypic' &&
              'You will be presented with another picture taken within 250 km (155 mi), 500km (311 mi), or 1000 km (621 mi) of the original picture.'}
            {selectedHint == 'dualdrop' &&
              'You will be allowed to drop a second pin on the map. Your score will be based on the better of your two guesses.'}
            {selectedHint == 'eraexposure' &&
              'You will be presented with another picture taken within 10 years of the original picture.'}
            {selectedHint == 'timetidbit' &&
              'You will be given a fact about the year the picture was taken.'}
            {!selectedHint && 'Click on the hint to learn what it does.'}
          </div>
        )}
        {props.mode == 'game' && selectedHint && (!usedHint || !hintData) && (
          <div className="mt-6 text-center">
            {usedHint && !hintData && (
              <button
                type="button"
                disabled
                className={`btn btn-primary text-lg px-9 font-normal lg:text-xl`}
              >
                Loading <Spinner zoom={0.4} />
              </button>
            )}
            {!usedHint && (
              <button
                type="button"
                onClick={() => {
                  setHintWait(true);
                  setTimeout(() => {
                    setHintWait(false);
                  }, 2000);
                  hints.useHint(selectedHint);
                }}
                className={`btn btn-primary text-lg px-9 font-normal lg:text-xl`}
              >
                {hintWait && <Spinner zoom={0.4} />}
                {!hintWait && !hints.shouldShowAd() && (
                  <>
                    Use hint <LightbulbOutlinedIcon htmlColor="currentColor" />
                  </>
                )}
                {!hintWait && hints.shouldShowAd() && (
                  <>
                    {!user.isPremium && (
                      <img
                        src="/assets/images/videoadicon.png"
                        className="w-8 h-8"
                      />
                    )}
                    Use hint <LightbulbOutlinedIcon htmlColor="currentColor" />
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default HintBox;
